/**
 * CadastreRefsSection — Onglet « Données » de l'admin : références cadastrales des parcelles.
 *
 * Lance le calcul de fond (/api/cadastre/batch) qui rattache chaque parcelle de La Réunion
 * aux parcelles cadastrales qu'elle recouvre, et affiche sa progression.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Map as MapIcon, Play, RefreshCw, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cadastre`;
const COL = { ink: "#1A1A1A", green: "#2D6A4F", brown: "#8B5E3C" };
const STATUS = {
  running: { label: "Calcul en cours", color: "#2D6A4F" },
  cancel_requested: { label: "Arrêt en cours…", color: "#B45309" },
  completed: { label: "Terminé", color: "#2D6A4F" },
  cancelled: { label: "Arrêté", color: "#7A7A7A" },
  failed: { label: "Interrompu (IGN indisponible)", color: "#B91C1C" },
  interrupted: { label: "Interrompu (redémarrage du serveur), reprise automatique sous 10 min", color: "#B45309" },
};
const fmt = (n) => (n ?? 0).toLocaleString("fr-FR");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : null);

export default function CadastreRefsSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/batch`);
      if (res.ok) setData(await res.json());
    } catch { /* réseau : on garde l'état affiché */ }
  }, []);

  const job = data?.job || {};
  const active = job.status === "running" || job.status === "cancel_requested";

  useEffect(() => { load(); }, [load]);
  // Pendant un calcul, la progression est rafraîchie toutes les 5 s
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [active, load]);

  const start = async (mode) => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/batch?mode=${mode}`, { method: "POST" });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Lancement impossible");
      toast.success(mode === "all" ? "Recalcul complet lancé" : "Calcul des références lancé");
      setTimeout(load, 1500);
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/batch/cancel`, { method: "POST" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Arrêt impossible");
      toast("Arrêt demandé : les parcelles déjà traitées restent enregistrées.");
      load();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  if (!data) {
    return (
      <section className="bg-white border border-black/5 rounded-xl p-5 flex items-center gap-2 text-xs text-neutral-500" data-testid="cadastre-refs-section">
        <Loader2 className="w-4 h-4 animate-spin" /> Références cadastrales…
      </section>
    );
  }

  const total = data.parcels_total || 0;
  const linked = data.parcels_linked || 0;
  const pctLinked = total ? Math.round((linked / total) * 100) : 0;
  const jobPct = job.total ? Math.min(100, Math.round(((job.done || 0) / job.total) * 100)) : 0;
  // ~0,7 parcelle par seconde (pause entre deux appels IGN + temps de réponse)
  const remainingMin = active && job.total ? Math.max(1, Math.round((job.total - (job.done || 0)) / 0.7 / 60)) : null;
  const st = STATUS[job.status];

  return (
    <section className="bg-white border border-black/5 rounded-xl p-5 space-y-3" data-testid="cadastre-refs-section">
      <div>
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: COL.ink }}>
          <MapIcon className="w-4 h-4" style={{ color: COL.brown }} />
          Références cadastrales des parcelles
        </h3>
        <p className="text-[11px] text-neutral-500 mt-0.5">
          Rattache chaque parcelle de La Réunion aux parcelles cadastrales qu&apos;elle recouvre
          (au moins {data.threshold_pct} % de sa surface). Source : <strong>IGN, API Carto cadastre</strong>.
        </p>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between items-baseline text-xs">
          <span className="text-neutral-500">Parcelles rattachées</span>
          <span className="font-bold tabular-nums" style={{ color: COL.green }} data-testid="cadastre-refs-linked">
            {fmt(linked)} / {fmt(total)} ({pctLinked} %)
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pctLinked}%`, background: COL.green }} />
        </div>
      </div>

      {st && (
        <div className="rounded-lg border border-black/5 bg-neutral-50/60 p-3 space-y-1.5 text-[11px]" data-testid="cadastre-refs-job">
          <div className="flex justify-between gap-2">
            <span className="font-semibold" style={{ color: st.color }}>{st.label}</span>
            <span className="text-neutral-500">{fmtDate(job.finished_at) ? `fin ${fmtDate(job.finished_at)}` : `début ${fmtDate(job.started_at)}`}</span>
          </div>
          {job.total != null && (
            <>
              <div className="h-1.5 rounded-full bg-white overflow-hidden border border-black/5">
                <div className="h-full transition-all" style={{ width: `${jobPct}%`, background: st.color }} />
              </div>
              <p className="text-neutral-600 tabular-nums">
                {fmt(job.done)} / {fmt(job.total)} parcelles traitées · {fmt(job.errors)} erreur{job.errors > 1 ? "s" : ""}
                {remainingMin && ` · environ ${remainingMin} min restantes`}
              </p>
            </>
          )}
          {job.last_error && <p className="text-red-700">{job.last_error}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {active ? (
          <Button onClick={cancel} disabled={busy || job.status === "cancel_requested"} size="sm" variant="outline"
            className="h-8 text-[11px] gap-1.5" data-testid="cadastre-refs-cancel">
            <Square className="w-3 h-3" /> Arrêter
          </Button>
        ) : (
          <>
            <Button onClick={() => start("missing")} disabled={busy || linked >= total} size="sm"
              className="h-8 text-[11px] gap-1.5 text-white" style={{ background: COL.green }} data-testid="cadastre-refs-start">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              {linked ? "Compléter les parcelles manquantes" : "Calculer les références cadastrales"}
            </Button>
            {linked > 0 && (
              <Button onClick={() => start("all")} disabled={busy} size="sm" variant="outline"
                className="h-8 text-[11px] gap-1.5" data-testid="cadastre-refs-start-all">
                <RefreshCw className="w-3 h-3" /> Tout recalculer
              </Button>
            )}
          </>
        )}
      </div>
      <p className="text-[10px] text-neutral-400 leading-relaxed">
        Le calcul interroge l&apos;IGN parcelle par parcelle, à rythme modéré : comptez environ une heure pour toute l&apos;île.
        Vous pouvez quitter la page. Il reprend seul après un redémarrage du serveur, et se relance chaque mois
        (2<sup>e</sup> dimanche) pour les parcelles nouvelles, modifiées ou calculées il y a plus de six mois.
        Une parcelle dont le contour change est recalculée aussitôt.
      </p>
    </section>
  );
}
